import {
  ALL_CATEGORIES,
  ANALYTICS_CATEGORIES,
  MONTH_OPTIONS,
  VIEW_OPTIONS,
  formatCategoryName,
  formatMuqamiName,
} from "./analyticsUtils";

const Control = ({ label, children, helper }) => (
  <label className="flex flex-col gap-2 min-w-[150px]">
    <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
      {label}
    </span>
    {children}
    {helper ? <span className="text-xs text-zinc-400">{helper}</span> : null}
  </label>
);

const selectClassName =
  "rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 outline-none transition focus:border-zinc-500";

const AnalyticsFilterBar = ({
  filters,
  yearOptions,
  maxMonth,
  muqamiOptions = ["ALL"],
  viewOptions = VIEW_OPTIONS,
  showMuqamiFilter = true,
  allowCompareYear = true,
  showLocalFundToggle = true,
  onChange,
  onReset,
}) => {
  const monthOptions = MONTH_OPTIONS.filter((month) => month.value <= maxMonth);
  const isMuqamiRanking = filters.view === "muqami";
  const isScopedMuqami = showMuqamiFilter && filters.muqami && filters.muqami !== "ALL";
  const categoryOptions = isMuqamiRanking
    ? [ALL_CATEGORIES, ...ANALYTICS_CATEGORIES]
    : ANALYTICS_CATEGORIES;

  return (
    <div className="rounded-3xl bg-white p-4 shadow">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
          <Control label="Year">
            <select
              className={selectClassName}
              value={filters.year}
              onChange={(e) => onChange("year", Number(e.target.value))}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </Control>

          <Control label="View Mode">
            <select
              className={selectClassName}
              value={filters.view}
              onChange={(e) => onChange("view", e.target.value)}
            >
              {viewOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Control>

          <Control
            label="Category"
            helper={
              isMuqamiRanking
                ? "Rank by all categories or one category."
                : filters.view !== "trend"
                ? "Highlights the selected category."
                : null
            }
          >
            <select
              className={selectClassName}
              value={filters.category}
              onChange={(e) => onChange("category", e.target.value)}
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {formatCategoryName(category)}
                </option>
              ))}
            </select>
          </Control>

          {showMuqamiFilter ? (
            <Control
              label="Muqami"
              helper={isMuqamiRanking ? "Ranking compares every Muqami." : null}
            >
              <select
                className={selectClassName}
                value={filters.muqami || "ALL"}
                disabled={isMuqamiRanking}
                onChange={(e) => onChange("muqami", e.target.value)}
              >
                {muqamiOptions.map((muqami) => (
                  <option key={muqami} value={muqami}>
                    {formatMuqamiName(muqami)}
                  </option>
                ))}
              </select>
            </Control>
          ) : null}

          <Control label="From Month">
            <select
              className={selectClassName}
              value={filters.fromMonth}
              onChange={(e) => onChange("fromMonth", Number(e.target.value))}
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </Control>

          <Control label="To Month">
            <select
              className={selectClassName}
              value={filters.toMonth}
              onChange={(e) => onChange("toMonth", Number(e.target.value))}
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </Control>

          {filters.view === "trend" && allowCompareYear ? (
            <Control
              label="Compare Year"
              helper={
                isScopedMuqami
                  ? "Available for All Muqami only."
                  : "Optional year-over-year line."
              }
            >
              <select
                className={selectClassName}
                value={filters.compareYear || ""}
                disabled={isScopedMuqami}
                onChange={(e) =>
                  onChange(
                    "compareYear",
                    e.target.value ? Number(e.target.value) : ""
                  )
                }
              >
                <option value="">None</option>
                {yearOptions
                  .filter((year) => year !== filters.year)
                  .map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
              </select>
            </Control>
          ) : showLocalFundToggle ? (
            <Control label="Compare Options">
              <button
                type="button"
                onClick={() =>
                  onChange("includeLocalFund", !filters.includeLocalFund)
                }
                className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm transition ${
                  filters.includeLocalFund
                    ? "border-zinc-900 bg-zinc-900 text-white"
                    : "border-zinc-200 bg-white text-zinc-700"
                }`}
              >
                <span>Include Local Fund</span>
                <span className="text-xs font-semibold">
                  {filters.includeLocalFund ? "On" : "Off"}
                </span>
              </button>
            </Control>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onReset}
            className="rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50"
          >
            Reset Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilterBar;
